import {
    addMinutes,
    asDate,
    differenceInMilliseconds,
    getTimestamp,
    now,
    startOfMinute,
    timestamp,
    today,
} from 'owa-datetime';
import { observableNowChanged, observableTodayChanged } from './actions';
import { MILLISECONDS_IN_MINUTE } from 'owa-date-constants';
import { orchestrator } from 'satcheljs';
import { setLocalTimeZone } from 'owa-datetime-store';
import { setNow, setToday } from './privateActions';
import getStore from './store';
import { getWindowData } from 'owa-window-data';
import { addToTimingMap } from 'owa-performance/lib/utils/timingMap';
import { logGreyError, logUsage } from 'owa-analytics';
import { getGlobalSettingsAccountMailboxInfo } from 'owa-account-source-list-store';

/**
 * If the initialization is called from multiple sources, ensure the times are updated
 * immediately on each call but prevent reinitializing the interval and listeners that
 * already exist
 */
let hasInitializeOwaObservableDateTime = false;

// Telemetry tracking for timer health monitoring
let intervalCallCount = 0;
let lastTelemetryLogTime = 0;
const TELEMETRY_LOG_INTERVAL_MS = 60 * MILLISECONDS_IN_MINUTE;

// Timers
/**
 * ID of the timeout timer that schedules the periodic update of 'now'
 */
let initialTimeoutId: ReturnType<typeof setTimeout> | undefined;
/**
 * ID of the interval timer that schedules the periodic update of 'now'
 */
let updateTimesIntervalId: ReturnType<typeof setInterval> | undefined;
/**
 * Id of the watchdog interval that monitors for stale main intervals
 * This background timer runs every 2 minutes to detect if the main interval
 * has stopped updating (e.g., due to browser tab suspension)
 */
let watchdogIntervalId: ReturnType<typeof setInterval> | undefined;

// Debounce and health
/**
 * Timestamp of the last value set for UpdateTimes to compare on when was last invoked
 * Too big of a drift here might indicate issues
 */
let lastUpdateTimesRunTimestamp = 0;
/**
 * Timestamp of the last timer restart to implement debounce protection.
 * Used to prevent rapid successive timer restarts when multiple recovery events
 * (focus, visibility change, watchdog) fire in quick succession.
 */
let lastRestartTime = 0;

// Timer Constants
const WATCHDOG_INTERVAL_MS = 2 * MILLISECONDS_IN_MINUTE;
const STALE_THRESHOLD_MS = 3 * MILLISECONDS_IN_MINUTE;
const STALE_UPDATE_THRESHOLD_MS = 5 * MILLISECONDS_IN_MINUTE;
/**
 * Minimum interval (in milliseconds) between timer restart operations.
 * Prevents creating multiple overlapping intervals when events fire rapidly.
 */
const MIN_RESTART_INTERVAL_MS = 1000;

function initializeUpdateTimesInterval() {
    // Clear any existing interval to prevent loops
    if (updateTimesIntervalId) {
        clearInterval(updateTimesIntervalId);
        updateTimesIntervalId = undefined;
    }

    updateTimesIntervalId = setInterval(() => {
        logObservableDateTimeHealth();
        updateTimes();
    }, MILLISECONDS_IN_MINUTE);
}

function initializeDateTimeTimeout() {
    // Clear any existing timeout to prevent loops
    if (initialTimeoutId) {
        clearTimeout(initialTimeoutId);
        initialTimeoutId = undefined;
    }

    const NOW = now();
    const startOfNextMinute = startOfMinute(addMinutes(NOW, 1));
    let msToNextMinute = differenceInMilliseconds(startOfNextMinute, NOW);

    // Ensure msToNextMinute is constrained to a one minute interval,
    // and immediately start setInterval otherwise by setting delay to 0
    if (
        msToNextMinute === undefined ||
        typeof msToNextMinute !== 'number' ||
        msToNextMinute < 0 ||
        msToNextMinute > MILLISECONDS_IN_MINUTE
    ) {
        msToNextMinute = 0;
    }

    initialTimeoutId = setTimeout(initializeUpdateTimesInterval, msToNextMinute);

    logGreyError(
        'ObservableDateTimeIntervalHealth',
        getGlobalSettingsAccountMailboxInfo(),
        undefined /* error */,
        {
            NOW: asDate(NOW).valueOf(),
            startOfNextMinute: asDate(startOfNextMinute).valueOf(),
            msToNextMinute,
            initialTimeoutId: JSON.stringify(initialTimeoutId),
        }
    );
}

/**
 * Safely restarts the timers with debounce protection.
 * Always updates times immediately, but debounces timer restart operations
 * to prevent multiple intervals when events fire in rapid succession.
 */
function safeRestartTimers() {
    updateTimes();

    const currentTime = Date.now();

    // Debounce the timer restart to prevent rapid firing
    if (currentTime - lastRestartTime >= MIN_RESTART_INTERVAL_MS) {
        lastRestartTime = currentTime;
        initializeDateTimeTimeout();
    }
}

export function initializeOwaObservableDateTime(): void {
    const mailboxInfo = getGlobalSettingsAccountMailboxInfo();
    logUsage('ObservableDateTimeInitialization', mailboxInfo);
    updateTimes();

    if (!hasInitializeOwaObservableDateTime) {
        initializeDateTimeTimeout();
        /**
         * If the tab loses visibility, the times will stop updating while in the background.
         * When the page restores focus, the next update will be up to 60s away, so we want
         * to have the time correct itself immediately instead of waiting.
         * Additionally, we restart the interval to ensure proper timing after backgrounding.
         */
        const doc = getWindowData()?.document;
        if (doc) {
            doc.addEventListener('visibilitychange', () => {
                logUsage('ObservableDateTimeUpdateOnVisibilityChange', mailboxInfo, {
                    currentTime: Date.now(),
                    isVisible: !doc.hidden,
                });
                safeRestartTimers();
            });
        }

        /**
         * If the tab regains focus, we want to ensure the time is updated immediately.
         */
        self.addEventListener('focus', () => {
            logUsage('ObservableDateTimeUpdateOnWindowFocus', mailboxInfo, {
                currentTime: Date.now(),
                calendarTime: asDate(now()).valueOf(),
            });
            safeRestartTimers();
        });

        /**
         * Runs every 2 minutes to check if the main interval has become stale (no updates for >3 minutes).
         * This catches cases where both visibility and focus events fail to fire.
         */
        if (!watchdogIntervalId) {
            watchdogIntervalId = setInterval(() => {
                const nowMs = Date.now();
                if (nowMs - lastUpdateTimesRunTimestamp > STALE_THRESHOLD_MS) {
                    logUsage('ObservableDateTimeForceUpdate', mailboxInfo, {
                        lastUpdate: lastUpdateTimesRunTimestamp,
                        currentTime: nowMs,
                    });
                    safeRestartTimers();
                }
            }, WATCHDOG_INTERVAL_MS);
        }

        hasInitializeOwaObservableDateTime = true;
    }
}

/** Recalculate 'today' when the time zone changes */
orchestrator(setLocalTimeZone, updateTimes);

/**
 * Update the store with the timestamp of now and
 * the start of the current day in the current time zone,
 * then raise public events so orchestrators that depend
 * on these values can run.
 */
function updateTimes() {
    addToTimingMap('observable', 'updateTimes');
    setNow();

    const curTimestamp = timestamp();
    // Check if we haven't run this for a long time, as that might signal an issue
    if (
        !!hasInitializeOwaObservableDateTime &&
        curTimestamp - lastUpdateTimesRunTimestamp > STALE_UPDATE_THRESHOLD_MS
    ) {
        // Log a warning or take some action
        logGreyError(
            'ObservableDateTimeStaleObservableNow',
            getGlobalSettingsAccountMailboxInfo(),
            undefined /* error */,
            {
                curTimestamp: new Date(curTimestamp).valueOf(),
                lastUpdateTimesRunTimestamp: new Date(lastUpdateTimesRunTimestamp).valueOf(),
                initialTimeoutId: JSON.stringify(initialTimeoutId),
                updateTimesIntervalId: JSON.stringify(updateTimesIntervalId),
            }
        );

        // If the interval has been stale for too long, restart it
        initializeDateTimeTimeout();
    }
    lastUpdateTimesRunTimestamp = curTimestamp;

    const newToday = getTimestamp(today());
    const isNewDay = getStore().today != newToday;
    if (isNewDay) {
        setToday(newToday);
    }

    observableNowChanged();
    if (isNewDay) {
        observableTodayChanged();
    }
}

function logObservableDateTimeHealth() {
    intervalCallCount++;
    const currentTime = Date.now();
    const timeSinceLastLog = currentTime - lastTelemetryLogTime;

    if (timeSinceLastLog >= TELEMETRY_LOG_INTERVAL_MS) {
        const expectedCalls =
            lastTelemetryLogTime === 0
                ? intervalCallCount
                : Math.round(timeSinceLastLog / MILLISECONDS_IN_MINUTE);

        logUsage('ObservableDateTimeIntervalHealth', getGlobalSettingsAccountMailboxInfo(), {
            currentTime,
            lastTelemetryLogTime,
            timeSinceLastLog,
            intervalCallCount,
            expectedCalls,
        });

        lastTelemetryLogTime = currentTime;
        intervalCallCount = 0; // Reset counter for next window
    }
}
